<script setup lang="ts">
import { ref, watch, computed, useSlots, onUnmounted } from 'vue'
import LabelText from './LabelText.vue'
import {
  CopyIcon,
  EyeIcon,
  EyeSlashIcon,
  CalendarIcon,
} from '@/icons';
import ErrorMessage from './ErrorMessage.vue';

// Define type for flatpickr instance without using 'any'
interface FlatpickrInstance {
  destroy: () => void;
  setDate: (date: string | Date, triggerChange?: boolean) => void;
  // Add other methods we might use
  open: () => void;
  close: () => void;
  clear: () => void;
  // For any other properties we don't explicitly define
  [key: string]: unknown;
}

const props = defineProps({
  modelValue: { type: [String, Date], default: '' },
  type: { type: String, required: true },
  id: { type: String, required: true },
  labelText: { type: String, default: '' },
  name: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  parentClass: { type: String, default: '' },
  icon: { type: String, default: '' },
  iconPosition: { type: String, default: 'right' },
  textSide: { type: String, default: '' },
  textPosition: { type: String, default: 'left' },
  size: { type: String as () => 'xss' | 'xs' | 'sm' | 'md' | 'lg', default: 'md' },
  iconsize: { type: String as () => 'xs' | 'sm' | 'md' | 'lg', default: 'md' },
  inputClass: { type: String, default: '' },
  isReadOnly: { type: Boolean, default: false },
  isTagInput: { type: Boolean, default: false },
  defaultValueTags: { type: Array as () => string[], default: () => [] },
  asterisk: { type: Boolean, default: false },
  note: { type: String, default: '' },
  noteClass: { type: String, default: '' },
  maxlength: { type: String },
  disabled: { type: Boolean, default: false },
  // New error-related props
  errorMessage: { type: [String, Array] as unknown as () => string | string[], default: '' },
  errorType: { type: String as () => 'error' | 'warning' | 'info', default: 'error' },
  // Datepicker specific props - simplified
  dateFormat: { type: String, default: 'Y-m-d' },
  showIcon: { type: Boolean, default: true },
})

const emit = defineEmits<{
  'update:modelValue': [value: string | Date]
}>()

const slots = useSlots()

const inputType = ref(props.type)
const value = ref<string | Date>(props.modelValue)
const showTooltip = ref(false)
const datepickerInstance = ref<FlatpickrInstance | null>(null)

// Watch for external modelValue changes
watch(() => props.modelValue, (newVal) => {
  value.value = newVal
})

// Tag logic
const tags = ref([...props.defaultValueTags])
const inputTag = ref('')

const addTag = () => {
  const tag = inputTag.value.trim()
  if (tag && !tags.value.includes(tag)) {
    tags.value.push(tag)
    emit('update:modelValue', tags.value.join(','))
    inputTag.value = ''
  }
}

const removeTag = (index: number) => {
  tags.value.splice(index, 1)
  emit('update:modelValue', tags.value.join(','))
}

// Clipboard and password
const copyToClipboard = () => {
  const textToCopy = String(value.value)
  navigator.clipboard.writeText(textToCopy).then(() => {
    showTooltip.value = true
    setTimeout(() => (showTooltip.value = false), 1500)
  })
}

const togglePassword = () => {
  inputType.value = inputType.value === 'password' ? 'text' : 'password'
}

// Check if input is a date type
const isDateInput = computed(() => {
  return props.type === 'date'
})

// Initialize flatpickr if needed
const initDatepicker = () => {
  if (isDateInput.value && props.showIcon && !datepickerInstance.value) {
    // Dynamically import flatpickr only when needed
    import('flatpickr').then((flatpickrModule) => {
      const flatpickr = flatpickrModule.default
      import('flatpickr/dist/flatpickr.css')

      const inputElement = document.getElementById(props.id) as HTMLInputElement
      if (inputElement && !datepickerInstance.value) {
        const instance = flatpickr(inputElement, {
          dateFormat: props.dateFormat,
          altInput: true,
          altFormat: 'd-m-Y',
          onChange: (selectedDates: Date[], dateStr: string) => {
            emit('update:modelValue', dateStr)
          },
        })

        // Cast to our interface
        datepickerInstance.value = instance as unknown as FlatpickrInstance
      }
    }).catch((error) => {
      console.error('Failed to load flatpickr:', error)
    })
  }
}

// Watch for ID changes to initialize datepicker
watch(() => props.id, () => {
  if (isDateInput.value && props.showIcon) {
    setTimeout(initDatepicker, 100) // Small delay to ensure DOM is ready
  }
}, { immediate: true })

// Update datepicker when modelValue changes
watch(() => props.modelValue, (newVal) => {
  value.value = newVal
  // If it's a date input and we have a datepicker instance, force update
  if (isDateInput.value && datepickerInstance.value) {
    // Clear the datepicker if value is empty
    if (!newVal || newVal === '') {
      datepickerInstance.value.clear()
    } else {
      datepickerInstance.value.setDate(newVal, false)
    }
  }
}, { immediate: true })

// Classes
const sizeClasses: Record<'xss' | 'xs' | 'sm' | 'md' | 'lg', string> = {
  xss: 'py-1 px-2 text-xs h-7',
  xs: 'py-1 px-2 text-xs h-9',
  sm: 'py-2 px-3 text-sm h-10',
  md: 'py-2.5 px-4 text-base h-11',
  lg: 'py-4 px-5 text-lg h-14'
}

// Check if icon exists (either via prop or slot)
const hasIcon = computed(() => {
  return !!props.icon || !!slots.icon || (isDateInput.value && props.showIcon)
})

// Check if it's a custom icon (slot) vs built-in icon (prop)
const isCustomIcon = computed(() => {
  return !props.icon && !!slots.icon
})

const wrapperClasses = computed(() => {
  const hasText = !!props.textSide

  if (!hasIcon.value && !hasText) return []

  const parts: string[] = ['flex items-stretch']

  // Add custom icon parent class
  if (isCustomIcon.value) {
    parts.push('has-custom-icon')
  }

  const iconRight = hasIcon.value && props.iconPosition === 'right'
  const textRight = hasText && props.textPosition === 'right'
  const iconLeft = hasIcon.value && props.iconPosition === 'left'
  const textLeft = hasText && props.textPosition === 'left'

  // Use flex-row-reverse when we have right-aligned elements
  if ((iconRight && !textLeft) || (textRight && !iconLeft)) {
    parts.push('flex-row-reverse')
  } else {
    parts.push('flex-row')
  }

  return parts
})

const textSideClasses = computed(() => [
  'h-11 text-gray-800 dark:text-white/90 bg-transparent dark:bg-gray-900 border border-gray-300 dark:border-gray-700',
  sizeClasses[props.size],
  props.textPosition === 'left' ? 'rounded-l-md' : 'rounded-r-md',
])

const inputClasses = computed(() => {
  const base = ['form-input', sizeClasses[props.size], props.inputClass]

  const hasText = !!props.textSide

  // If custom icon, always use rounded-md on all corners
  if (isCustomIcon.value) {
    base.push('rounded-md')
  }
  // Normal logic for built-in icons and text sides
  else if (!hasIcon.value && !hasText) {
    base.push('rounded-md')
  } else {
    const iconRight = hasIcon.value && props.iconPosition === 'right'
    const textRight = hasText && props.textPosition === 'right'
    const isReversed = iconRight || textRight

    const leftFlat = (hasIcon.value && props.iconPosition === 'left') || (hasText && props.textPosition === 'left')
    const rightFlat = (hasIcon.value && props.iconPosition === 'right') || (hasText && props.textPosition === 'right')

    if (leftFlat && rightFlat) {
      base.push('rounded-none')
    } else if (leftFlat && !isReversed) {
      base.push('rounded-l-none', 'rounded-r-md')
    } else if (rightFlat && !isReversed) {
      base.push('rounded-r-none', 'rounded-l-md')
    } else if (leftFlat && isReversed) {
      base.push('rounded-r-none', 'rounded-l-md')
    } else if (rightFlat && isReversed) {
      base.push('rounded-l-md', 'rounded-r-0')
    }
  }

  if (props.disabled) {
    base.push('cursor-not-allowed bg-gray-100 dark:bg-gray-800')
  }

  return base
})

const iconClasses = computed(() => {
  const base = [
    'flex items-center h-11 text-gray-800 dark:text-white/90 bg-transparent dark:bg-gray-900 border border-gray-300 dark:border-gray-700',
    sizeClasses[props.size],
  ]

  // For custom icons, use rounded-md, for built-in icons use normal logic
  if (slots.icon) {
    base.push('rounded-md')
  } else {
    base.push(props.iconPosition === 'left' ? 'rounded-l-md' : 'rounded-r-md')
  }

  if (props.disabled) {
    base.push('opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800')
  }

  return base
})

// Simple error check
const hasError = computed(() => {
  return !!props.errorMessage &&
    (typeof props.errorMessage === 'string' ? props.errorMessage.length > 0 : props.errorMessage.length > 0)
});

// Simple dynamic border class
const dynamicInputClasses = computed(() => {
  if (hasError.value && props.errorType === 'error') {
    return 'has-error'
  }
  return ''
});

// Datepicker input classes
const datepickerClasses = computed(() => [
  'dark:bg-dark-900 h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent bg-none px-4 py-2.5 pl-4 pr-11 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800',
  props.disabled ? 'cursor-not-allowed opacity-60' : '',
  hasError.value && props.errorType === 'error' ? 'border-red-500 focus:border-red-500' : ''
])

// Clean up
onUnmounted(() => {
  if (datepickerInstance.value) {
    datepickerInstance.value.destroy()
    datepickerInstance.value = null
  }
})
</script>

<template>
  <div :class="['input-wrapper flex flex-col gap-2', parentClass]">
    <!-- Label -->
    <LabelText
      v-if="labelText"
      :labelText="labelText"
      :inputId="id"
      :asterisk="asterisk"
    />

    <!-- Input block -->
    <div :class="wrapperClasses">
      <!-- Optional prefix/suffix text -->
      <span v-if="textSide" :class="textSideClasses">{{ textSide }}</span>

      <!-- Icon section -->
      <template v-if="icon === 'copy'">
        <button
          type="button"
          :class="iconClasses"
          @click="copyToClipboard"
          :aria-label="'Copy to clipboard'"
          :disabled="disabled"
        >
          <transition name="fade">
            <span v-if="showTooltip" class="tooltip-custom bottom">Copied!</span>
          </transition>

          <CopyIcon />
        </button>
      </template>

      <template v-else-if="icon === 'eye'">
        <button
          type="button"
          :class="iconClasses"
          @click="togglePassword"
          :aria-label="'Toggle password visibility'"
          :disabled="disabled"
        >
          <EyeIcon v-if="inputType === 'password'" />
          <EyeSlashIcon v-else />
        </button>
      </template>

      <template v-else-if="slots.icon">
        <span :class="['custom-icon-wrapper', `size-${iconsize}`, iconClasses]">
          <slot name="icon"></slot>
        </span>
      </template>

      <!-- Date Input with icon -->
      <div v-if="isDateInput && showIcon" class="relative w-full">
        <input
          :type="inputType"
          :id="id"
          :name="name"
          :placeholder="placeholder"
          :readonly="isReadOnly"
          v-model="value"
          @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
          :class="[datepickerClasses, dynamicInputClasses]"
          :aria-labelledby="labelText ? id : undefined"
          :aria-label="labelText ? undefined : (placeholder || name || 'Input field')"
          :disabled="disabled"
          :maxlength="maxlength"
          data-input
        />
        <span
          v-if="showIcon"
          class="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400"
        >
          <CalendarIcon class="w-5 h-5 fill-current" />
        </span>
      </div>

      <!-- Tag Input -->
      <div v-else-if="isTagInput" class="flex flex-col w-full gap-2">
        <input
          v-model="inputTag"
          @keydown.enter.prevent="addTag"
          :class="['form-input rounded-md', sizeClasses[props.size], inputClass]"
          :placeholder="placeholder || 'Type and press enter...'"
          :aria-labelledby="labelText ? id : undefined"
          :aria-label="labelText ? undefined : (placeholder || name || 'Input field')"
          :disabled="disabled"
        />
        <div v-if="tags.length" class="flex flex-wrap gap-2">
          <span
            v-for="(tag, index) in tags"
            :key="index"
            class="bg-blue-500 text-white px-3 py-1.5 rounded-full flex items-center"
          >
            {{ tag }}
            <button @click="removeTag(index)" class="ml-2 text-white font-bold" :aria-label="'Remove tag ' + tag">×</button>
          </span>
        </div>
      </div>

      <!-- Normal Input -->
      <input
        v-else
        :type="inputType"
        :id="id"
        :name="name"
        :placeholder="placeholder"
        :readonly="isReadOnly"
        v-model="value"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        :class="[inputClasses, dynamicInputClasses]"
        :aria-labelledby="labelText ? id : undefined"
        :aria-label="labelText ? undefined : (placeholder || name || 'Input field')"
        :disabled="disabled"
        :maxlength="maxlength"
      />
    </div>

    <!-- Note -->
    <p
      v-if="note"
      v-html="note"
      :class="['text-xs font-normal', noteClass]"
    ></p>

    <!-- Error message below input (default position) -->
    <ErrorMessage
      :message="errorMessage"
      :type="errorType"
    />
  </div>
</template>

<style>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }
  .tooltip-custom {
    position: absolute;
    bottom: -1.5rem;
    left: 50%;
    transform: translateX(-50%);
    background-color: black;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }
  .custom-icon-wrapper.size-md svg {
    width: 20px;
    height: 20px;
  }
  /* Optional size variants */
  .custom-icon-wrapper.size-xs :deep(svg) {
    width: 16px;
    height: 16px;
  }
  .custom-icon-wrapper.size-sm :deep(svg) {
    width: 18px;
    height: 18px;
  }
  .custom-icon-wrapper.size-lg svg {
    width: 24px;
    height: 24px;
  }
</style>
